'use client';
import Image from 'next/image';
import EffortRating from '@components/shared/effort-rating.jsx';
import { ToutContent, ToutContentButtons } from '@repo/ui/src/components/ToutContent';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ButtonStyleEnum, ButtonIconEnum } from '@enums/button-enums.js';
import { AuthorProps, RecipeMetaSectionProps, MetaProps } from '@repo/ui/src/types';
import { CurrentUrlProps } from '@repo/ui/src/types/currentUrl';
import { useIsSM } from '@repo/ui/src/utils/useMediaQuery';

interface RecipeCardProps {
  _meta: MetaProps;
  assets?: Array<{
    endpoint: string;
    name: string;
  }>;
  author?: AuthorProps;
  recipeMetaSection?: RecipeMetaSectionProps;
  heading?: string;
  link?: string;
  linkLabel?: string;
  by?: string;
  prepTimeLabel?: string;
  cookTimeLabel?: string;
  effort?: string;
  outOf?: string;
  currentUrl?: CurrentUrlProps;
}

const RecipeCard = ({
  _meta,
  assets,
  author,
  recipeMetaSection,
  heading,
  link,
  linkLabel,
  by,
  prepTimeLabel,
  cookTimeLabel,
  effort,
  outOf,
}: RecipeCardProps) => {
  const isSm = useIsSM();

  const buttons = [
    {
      link: { link: _meta.deliveryKey, label: linkLabel, displayName: link },
      style: ButtonStyleEnum.Text,
      icon: ButtonIconEnum.Skewer,
    },
  ];

  const imgUrl = buildImageSrc(
    assets?.length ? assets[0] : null,
    isSm ? 700 : 600,
    isSm ? 9 : 1,
    isSm ? 10 : 1,
  );

  const authorImgUrl = buildImageSrc(author?.avatar, 37, 1, 1);

  const logBackground = {
    endpoint: 'traeger',
    name: 'log-cropped-transparent',
  };

  const logBackgroundImgUrl = buildImageSrc(logBackground, 500, 0, 0);

  if (!recipeMetaSection) return null;

  return (
    <div className="tw-flex tw-flex-wrap tw-h-full tw-flex-row sm:tw-flex-row-reverse tw-overflow-hidden">
      <div className="tw-w-full tw-h-full sm:tw-w-1/2">
        <Image
          src={imgUrl}
          alt={recipeMetaSection.title || ""}
          width={isSm? 700 : 600}
          height={isSm ? 700 : 600}
          className="tw-h-full tw-w-full"
          priority
        />
      </div>
      <div className="tw-w-full sm:tw-w-1/2 tw-relative tw-overflow-hidden">
        <Image
          src={logBackgroundImgUrl}
          role="presentation"
          alt=""
          width={500}
          height={200}
          className="tw-absolute tw-bottom-0 tw-left-[5%] tw-w-[40%] tw-z-[-1] sm:tw-w-[90%]"
          aria-hidden="true"
        />

        <div className="tw-p-4 md:tw-p-gutterLG">
          {heading && (
            <ToutContent
              align="left"
              heading={recipeMetaSection.title}
              headingStyleVariant="h2"
              lilHeading={heading}
              className="tw-pb-6"
            />
          )}
          {author && (
            <div className="tw-flex tw-items-center tw-mb-3">
              <div className="tw-mr-2 tw-relative tw-w-[30px] tw-h-[30px] sm:tw-w-[37px] sm:tw-h-[37px]">
                <Image
                  src={authorImgUrl}
                  alt={author.name || ""}
                  fill
                  className="tw-rounded-full tw-object-cover"
                />
              </div>
              <div className="tra-text-web-body-xl">
                {by} {author.name}
              </div>
            </div>
          )}
          <ToutContent
            align="left"
            lineClamp={4}
            description={recipeMetaSection.description}
            className="tw-pb-6"
          />
          <div className="tw-mt-1 tw-flex tw-items-center tw-justify-evenly tw-bg-neutral-700 tw-text-neutral-100 tw-mx-[-1rem] tw-p-4 md:tw-mx-[-7.5vw] md:tw-px-[7.5vw]">
            <div className="tw-flex tw-items-center">
              <span className="tra-text-web-heading-4 tw-pr-1">{recipeMetaSection.prepTime}</span>
              <span className="tra-text-web-utl-label-s tw-w-6">{prepTimeLabel}</span>
            </div>
            <div className="tw-w-px tw-h-10 tw-bg-warm-200 tw-mx-[0.2rem] lg:tw-mx-6"></div>
            <div className="tw-flex tw-items-center">
              <span className="tra-text-web-heading-4 tw-pr-1">{recipeMetaSection.cookTime}</span>
              <span className="tra-text-web-utl-label-s tw-w-6">{cookTimeLabel}</span>
            </div>
            {isSm && <div className="tw-w-px tw-h-10 tw-bg-warm-200 tw-mx-[0.2rem] lg:tw-mx-6"></div>}
            <div>
              <div className="tw-flex tw-items-center">
                {effort && (
                  <span className="tra-text-web-utl-label-s">{`${effort}:`}</span>
                )}
                <EffortRating
                  complexity={recipeMetaSection.complexity}
                  iconWidth="16px"
                  effortText={effort}
                  outOfText={outOf}
                />
              </div>
            </div>
          </div>
          <ToutContentButtons className="tw-mt-1" buttons={buttons} align="left" />
        </div>
      </div>
    </div>
  );
};

export default RecipeCard;